import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Banknote, Sparkles, Pencil, ArrowUpRight, FileText, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Stepper } from "@/pages/formation/FormationSteps";
import {
  ImportStep, IntakeStep, ReadinessStep, MatchesStep, DocumentsStep, ApplicationsStep,
  GenerateStep, FundingReviewStep, FundingDashboard,
} from "@/pages/funding/FundingSteps";

function CompletedView({ wf, onEdit }) {
  const navigate = useNavigate();
  const r = wf.readiness;
  return (
    <div className="fv-fade-up max-w-4xl" data-testid="funding-completed">
      <PageHeader eyebrow="Build & Fund" title="Funding Center"
        subtitle="Your funding readiness is prepared and synced across Fairview Systems. Below is your dashboard and recommended next action." />
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Banknote className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div className="flex-1"><div className="font-heading text-2xl text-white">Funding readiness: {r.score}% — {r.label}</div><div className="text-sm text-[#A1A1AA]">Estimated range: {r.estimated_range}</div></div>
        <button onClick={onEdit} data-testid="funding-edit-btn" className="flex items-center gap-1.5 text-sm h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors shrink-0"><Pencil className="w-4 h-4" /> Edit</button>
      </div>
      <FundingDashboard d={wf.dashboard} />
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-6" data-testid="funding-plan-final">
        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Recommended next action</h3></div>
        <p className="text-sm text-[#D4D4D8] mb-1">Best-fit path: <span className="text-[#D4AF37]">{wf.ai_review.best_path}</span></p>
        <ul className="space-y-1.5 mt-2">{wf.ai_review.improvement_plan.map((p, i) => <li key={i} className="text-sm text-[#A1A1AA] flex gap-2"><ArrowRight className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37] shrink-0" />{p.step}</li>)}</ul>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <button onClick={() => navigate("/documents")} data-testid="funding-next-docs" className="text-left rounded-xl border border-white/8 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors flex items-start gap-3"><FileText className="w-5 h-5 text-[#D4AF37] shrink-0" /><div><div className="text-white text-sm font-medium">Funding documents</div><div className="text-xs text-[#71717A]">Reports saved to your Document Library.</div></div></button>
        <button onClick={() => navigate("/ai-assistant")} data-testid="funding-next-ai" className="text-left rounded-xl border border-[#D4AF37]/30 bg-[#151310] p-4 hover:border-[#D4AF37]/50 transition-colors flex items-start gap-3"><Sparkles className="w-5 h-5 text-[#D4AF37] shrink-0" /><div><div className="text-white text-sm font-medium">Ask the AI Advisor</div><div className="text-xs text-[#71717A]">Draft a funding use statement or lender summary.</div></div></button>
      </div>
    </div>
  );
}

export default function Funding() {
  const [wf, setWf] = useState(null);
  const [step, setStep] = useState("import");
  const [intake, setIntake] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/funding/workflow");
    setWf(data); setIntake(data.intake || {}); setStep(data.workflow.current_step || "import");
  }, []);
  useEffect(() => { load(); }, [load]);

  const persist = async (s, advance) => {
    const payload = s === "intake" ? { intake } : {};
    const { data } = await api.put("/funding/workflow/step", { step: s, data: payload, advance });
    setWf((w) => ({ ...w, ...data }));
    return data;
  };
  const goNext = async () => {
    setSaving(true);
    try { await persist(step, true); const i = wf.steps.findIndex((x) => x.id === step); setStep(wf.steps[Math.min(i + 1, wf.steps.length - 1)].id); window.scrollTo(0, 0); }
    catch { toast.error("Could not save. Please try again."); } finally { setSaving(false); }
  };
  const goBack = async () => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Funding autosave deferred:", e); }
    setSaving(false); const i = wf.steps.findIndex((x) => x.id === step); setStep(wf.steps[Math.max(i - 1, 0)].id); window.scrollTo(0, 0);
  };
  const jump = async (target) => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Funding autosave deferred:", e); }
    setSaving(false); setStep(target); window.scrollTo(0, 0);
  };

  const refresh = async () => { const { data } = await api.get("/funding/workflow"); setWf(data); };
  const setDocStatus = async (docId, status) => {
    try { const { data } = await api.put("/funding/workflow/document", { doc_id: docId, status }); setWf((w) => ({ ...w, documents: data.documents })); }
    catch { toast.error("Could not update document"); }
  };
  const addApp = async (payload) => { try { await api.post("/funding/workflow/applications", payload); await refresh(); toast.success("Application added"); } catch { toast.error("Could not add application"); } };
  const updateApp = async (id, patch) => {
    try { const { data } = await api.patch(`/funding/workflow/applications/${id}`, patch); await refresh(); toast.success("Application updated"); return data; }
    catch { toast.error("Could not update application"); return null; }
  };
  const deleteApp = async (id) => { try { await api.delete(`/funding/workflow/applications/${id}`); await refresh(); toast.success("Removed"); } catch { toast.error("Could not remove"); } };

  const generateDocs = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/funding/workflow/generate-documents"); setWf((w) => ({ ...w, documents_generated: data.documents.map((d) => d.section) })); toast.success(`${data.documents.length} documents saved to your Document Library`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate documents"); } finally { setGenerating(false); }
  };
  const complete = async () => {
    setSaving(true);
    try { const { data } = await api.post("/funding/workflow/complete"); setWf((w) => ({ ...w, ...data })); setEditing(false); toast.success("Funding readiness complete! 🎉"); window.scrollTo(0, 0); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); } finally { setSaving(false); }
  };

  if (!wf) return <p className="text-[#71717A]">Loading…</p>;
  if (wf.workflow.status === "completed" && !editing) return <CompletedView wf={wf} onEdit={() => { setEditing(true); setStep("import"); }} />;

  const completed = wf.workflow.completed_steps || [];
  const meta = wf.steps.find((s) => s.id === step);
  const idx = wf.steps.findIndex((s) => s.id === step);
  const pct = Math.round((completed.length / wf.steps.length) * 100);

  const STEP_UI = {
    import: <ImportStep wf={wf} />,
    intake: <IntakeStep intake={intake} setIntake={setIntake} wf={wf} />,
    readiness: <ReadinessStep wf={wf} />,
    matches: <MatchesStep wf={wf} />,
    documents: <DocumentsStep wf={wf} onSetStatus={setDocStatus} />,
    applications: <ApplicationsStep wf={wf} onAdd={addApp} onUpdate={updateApp} onDelete={deleteApp} />,
    generate: <GenerateStep hasDocs={(wf.documents_generated || []).length > 0} onGenerate={generateDocs} generating={generating} />,
    review: <FundingReviewStep review={wf.ai_review} onComplete={complete} completing={saving} />,
  };

  return (
    <div className="fv-fade-up max-w-4xl" data-testid="funding-wizard">
      <PageHeader eyebrow="Build & Fund" title="Funding Center"
        subtitle="Evaluate your funding readiness, prepare documents, and manage applications. Progress saves automatically." />
      <FundingDashboard d={wf.dashboard} />
      <div className="rounded-2xl border border-white/8 bg-[#121214] p-4 mb-6">
        <div className="flex justify-between text-xs mb-2"><span className="text-[#A1A1AA]">Overall progress</span><span className="text-[#D4AF37]" data-testid="funding-overall-pct">{pct}%</span></div>
        <Progress value={pct} className="h-2 bg-white/5" />
      </div>
      <Stepper steps={wf.steps} current={step} completed={completed} onJump={jump} />
      <div className="mb-2">
        <div className="text-[11px] uppercase tracking-wider text-[#71717A]">Step {idx + 1} of {wf.steps.length}</div>
        <h2 className="font-heading text-2xl text-white">{meta.title}</h2>
        <p className="text-sm text-[#71717A]">{meta.desc}</p>
      </div>
      <div className="mt-5 pb-24" data-testid="funding-step-content">{STEP_UI[step]}</div>
      <div className="flex items-center justify-between gap-3 pt-6 border-t border-white/8 sticky bottom-0 bg-[#0A0A0B]/80 backdrop-blur pb-2">
        <button onClick={goBack} disabled={idx === 0 || saving} data-testid="funding-back-btn" className="flex items-center gap-1.5 text-sm h-11 px-5 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"><ArrowLeft className="w-4 h-4" /> Back</button>
        {step !== "review" && <button onClick={goNext} disabled={saving} data-testid="funding-next-btn" className="flex items-center gap-1.5 fv-gold-gradient text-[#0A0A0B] rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-40">{saving ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}
